import { LoadingOutlined, MenuOutlined, RightOutlined, UpOutlined } from "@ant-design/icons"
import { PriceIntervalEnum, Workflow, WorkflowConditionEnum, WorkflowRule, WorkflowTriggerEnum } from "@finway-group/shared/lib/models"
import { Button, Col, Empty, Modal, Row, Steps, Table, Tag } from "antd"
import { ColumnsType } from "antd/es/table"
import React, { useEffect, useRef, useState } from "react"
import { Edit as EditIcon, Plus as PlusIcon, ChevronRight as RightIcon, Trash2 as TrashIcon } from "react-feather"
import { Helmet } from "react-helmet"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useParams } from "react-router"
import { SortableContainer, SortableElement, SortableHandle } from "react-sortable-hoc"
import { useDebouncedCallback } from "use-debounce/lib"

import Filter from "Components/filter/filter"
import FilterSection from "Components/filterSection"
import WorkflowModal from "Components/modals/workflow.modal"
import NoSearchDataFound from "Components/NoSearchDataFound"
import PriceLabel from "Components/priceLabel"
import SearchInput from "Components/searchInput"
import UserImage from "Components/UserImage"
import { useCompany } from "Shared/hooks/company.hooks"
import { useSubCostCenters } from "Shared/hooks/costCenter.hooks"
import { useCostCenters2 } from "Shared/hooks/costCenter2.hooks"
import { useEmployees } from "Shared/hooks/employee.hooks"
import { useFetchTable, useTable, useTableFilterQueryString } from "Shared/hooks/table.hooks"
import { useVendors } from "Shared/hooks/vendor.hooks"
import { NotificationService } from "Shared/services"
import { NotificationTypeEnum } from "Shared/services/notification.service"
import WorkflowService from "Shared/services/workflowService"
import { refetchTable, updateTableOnChange, updateTableSearch } from "Shared/store/actions/tables/tableActions"
import { fetchAllWorkflows, setWorkflow, updateWorkflow } from "Shared/store/actions/workflow/workflowActions"
import { TablesEnum } from "Shared/store/reducers/tableConfigReducer"
import { applyFilter } from "Shared/utils/filter.utils"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"
import { isTableFilterApplied } from "Shared/utils/table.utils"
import { GREEN } from "App.scss"

import { getApproverLabelData } from "./workflow.utils"
import WorkflowConditionTag from "./workflowConditionTag"

interface WorkflowsInterface {}

const Workflows: React.FC<WorkflowsInterface> = ({}) => {
    const { id } = useParams<{ id: string }>()
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const [isModalShowing, setIsModalShowing] = useState(false)
    const [expandedRows, setExpandedRows] = useState<any[]>([])
    const [isLoading, setIsLoading] = useStateIfMounted(false)
    const [isFilterModalShowing, setIsFilterModalShowing] = useState<boolean>(false)

    const workflowsTable = useTable<Workflow>(TablesEnum.WORKFLOWS)
    const costCenters = useSubCostCenters(true)
    const vendors = useVendors(true)
    const employees = useEmployees(true)
    const costCenters2 = useCostCenters2()
    const isCreateBtnEnabled = costCenters.length > 0 || vendors.length > 0 || employees.length > 0
    const filterQueryString = useTableFilterQueryString(TablesEnum.WORKFLOWS)
    const dragAndDropAnchor = useRef(null)
    const { showCostCentersIds } = useCompany()

    useFetchTable(TablesEnum.WORKFLOWS)
    useEffect(() => {
        fetchWorkflows()
    }, [filterQueryString])

    const fetchWorkflows = async () => {
        await fetchAllWorkflows()
        dispatch(refetchTable(TablesEnum.WORKFLOWS))
    }

    useEffect(() => {
        if (id) setExpandedRows([id])
    }, [id])

    const onDragEnd = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
        const draggedWorkflow = workflowsTable.data.docs[oldIndex]
        const targetWorkflow = workflowsTable.data.docs[newIndex]
        if (draggedWorkflow.order === targetWorkflow.order) return

        const updatedWorkflow = { ...draggedWorkflow, order: targetWorkflow.order }

        setIsLoading(true)

        // Start performing updates to server
        const performUpdate = async () => {
            await updateWorkflow(updatedWorkflow?._id, updatedWorkflow)(dispatch)
            await fetchAllWorkflows()(dispatch)
            dispatch(refetchTable(TablesEnum.WORKFLOWS))
        }

        performUpdate()
            .catch((e) => NotificationService.showErrorNotificationBasedOnResponseError(e, t("error:update.title"), t("error:update.message")))
            .finally(() => {
                setIsLoading(false)
            })
    }

    // Preparing draggable wrapper for antd table
    // TODO : we can utilize hooks to generate these
    const DragHandle = SortableHandle(() => <MenuOutlined style={{ cursor: "grab", color: "#999" }} />)
    const SortableItem = SortableElement((props: any) => <tr {...props} />)
    const SortableWrapper = SortableContainer((props: any) => <tbody {...props} />)
    const DraggableWrapper = (props: any) => {
        //
        return <SortableWrapper helperContainer={dragAndDropAnchor.current} helperClass="table-row-dragging" useDragHandle disableAutoscroll onSortEnd={onDragEnd} {...props} />
    }

    const DraggableBodyRow = ({ className, ...restProps }: any) => {
        const index = workflowsTable.data.docs.findIndex((workflow) => workflow.id === restProps["data-row-key"])
        return <SortableItem index={index} className={className} {...restProps} />
    }

    const onEditWorkflow = async (id: string) => {
        const workflow = workflowsTable.data.docs.find((workflow) => workflow.id === id)
        await setWorkflow(workflow)(dispatch)
        setIsModalShowing(true)
    }

    const handleSearch = useDebouncedCallback((searchString) => dispatch(updateTableSearch(TablesEnum.WORKFLOWS, searchString)), 800)

    const onDeleteWorkflow = (id: string) => {
        Modal.confirm({
            title: t("confirm:workflow.delete.title"),
            content: t("confirm:workflow.delete.message"),
            okText: t("confirm:workflow.delete.confirm"),
            cancelText: t("confirm:workflow.delete.cancel"),
            type: "warning",
            async onOk() {
                try {
                    await WorkflowService.deleteWorkflow(id)
                    // refetch all rules in store
                    await dispatch(fetchAllWorkflows())
                    dispatch(refetchTable(TablesEnum.WORKFLOWS))
                    NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:workflow.deleted.title"), t("notification:workflow.deleted.message"))
                } catch (err) {
                    NotificationService.showErrorNotificationBasedOnResponseError(err, NotificationTypeEnum.ERROR, t("error:error"))
                }
            },
        })
    }

    const columns: ColumnsType = [
        {
            title: "Order",
            dataIndex: "sort",
            width: 30,
            className: "drag-visible",
            render: () => (
                <div className="mb-4">
                    <DragHandle />
                </div>
            ),
        },
        {
            key: "name",
            title: t("label:name"),
            render: (workflow: Workflow) => (
                <span className="font-bold">
                    #{workflow.order} {workflow.name}
                </span>
            ),
        },
        {
            key: "steps",
            title: t("label:steps"),
            render: ({ _id, rules }: Workflow) => (
                <>
                    <Tag className="ant-tag ant-tag-green-highlight pb-2" color="primary">
                        {rules.length}
                    </Tag>
                    <span className="ml-8">{t("label:steps")}</span>
                </>
            ),
        },
        {
            key: "triggerType",
            title: t("label:workflow.trigger.trigger_type"),
            render: ({ isGeneralized, triggerType }: Workflow) => (
                <div className="flex flex-col">
                    <span>{t("label:workflow.trigger.trigger_type")}</span>
                    <div>
                        <span className="font-bold">{t(`label:workflow.trigger.${triggerType.toLowerCase()}`)}</span>
                        {triggerType !== WorkflowTriggerEnum.AMOUNT && (
                            <Tag className="ant-tag ant-tag-blue pb-2 ml-8">{isGeneralized ? t("label:workflow.trigger.general") : t("label:workflow.trigger.specific")}</Tag>
                        )}
                    </div>
                </div>
            ),
        },
        {
            key: "scope",
            title: t("label:workflow.scope.scope"),
            render: ({ scope }: Workflow) => (
                <div className="flex flex-col">
                    <span>{t("label:workflow.scope.scope")}</span>
                    <span className="font-bold">{t(`label:workflow.scope.${scope}`)}</span>
                </div>
            ),
        },
        {
            key: "action",
            title: t("label:action"),
            width: 100,
            render: (workflow: Workflow) => (
                <>
                    <Button className="mr-4" onClick={() => onEditWorkflow(workflow._id)}>
                        <EditIcon />
                    </Button>

                    <Button onClick={() => onDeleteWorkflow(workflow._id)}>
                        <TrashIcon />
                    </Button>
                </>
            ),
        },
    ]

    const renderWorkflowDetails = (workflow: Workflow) => {
        return (
            <div className="workflows ml-10 mt-20">
                <Row gutter={[16, 16]}>
                    <Col className="font-bold">{t("label:workflow.trigger.trigger_objects")}:</Col>
                    <Col>{getObjectLabelForTriggerType(workflow)}</Col>
                </Row>

                <Steps className="mt-20" direction="vertical" size="small" current={-1}>
                    {workflow.rules
                        .sort((a: WorkflowRule, b: WorkflowRule) => a.order - b.order)
                        .map((rule: WorkflowRule) => {
                            const approver = getApproverLabelData(rule.approverData)
                            const secondApprover = getApproverLabelData(rule.secondApprover)

                            return (
                                <Steps.Step
                                    key={rule.order}
                                    title={
                                        <div className="flex items-center -mt-3">
                                            <span>{t("label:workflow.approver")}</span>
                                            <RightIcon size={20} className="ml-4 mr-4 mt-2" color={GREEN} />
                                            {approver && (
                                                <div className="flex items-center">
                                                    <UserImage user={approver.user} />
                                                    <span>{approver.label}</span>
                                                </div>
                                            )}
                                            {rule.condition && <WorkflowConditionTag condition={rule.condition} />}
                                            {secondApprover && (
                                                <div className="flex items-center">
                                                    <UserImage user={secondApprover.user} />
                                                    <span>{secondApprover.label}</span>
                                                </div>
                                            )}
                                            {rule.condition === WorkflowConditionEnum.IF && rule.amount && (
                                                <div className="mt-2">
                                                    <PriceLabel value={rule.amount} currency={rule.currency} interval={PriceIntervalEnum.ONE_TIME} />
                                                </div>
                                            )}
                                        </div>
                                    }
                                    status="process"
                                />
                            )
                        })}
                </Steps>
            </div>
        )
    }

    // prettier-ignore
    const getObjectLabelForTriggerType = (workflow: Workflow) => {
        if (workflow.isGeneralized) return <span>{t('label:workflow.trigger.all')}</span>

        const lastIndex = workflow.triggerReferenceIds.length - 1
        switch (workflow.triggerType) {
            case WorkflowTriggerEnum.COST_CENTER:
                return costCenters.filter(({_id}) => workflow.triggerReferenceIds.includes(_id)).map((cc, index) => <span key={index} className="mr-4">{`${showCostCentersIds ? "ID " + cc.id + " - " : ""}${cc.name}`}{index !== lastIndex ? ',' : ''}</span>)
            case WorkflowTriggerEnum.COST_CENTER_2:
                return costCenters2.filter(({_id}) => workflow.triggerReferenceIds.includes(_id)).map((cc2, index) => <span key={index} className="mr-4">{cc2.name}{index !== lastIndex ? ',' : ''}</span>)
            case WorkflowTriggerEnum.VENDOR:
                return vendors.filter(({id}) => workflow.triggerReferenceIds.includes(id)).map((v, index) => <span key={index} className="mr-4">{v.name}{index !== lastIndex ? ',' : ''}</span>)
            case WorkflowTriggerEnum.USER:
                return employees.filter(({id}) => workflow.triggerReferenceIds.includes(id)).map((e, index) => <span key={index} className="mr-4">{`${e.firstName} ${e.lastName}`}{index !== lastIndex ? ',' : ''}</span>)
            case WorkflowTriggerEnum.AMOUNT:
                return <><span className="mr-6">&gt;</span><PriceLabel value={workflow.triggerReferenceValue} currency={workflow.triggerReferenceCurrency} interval={PriceIntervalEnum.ONE_TIME} /></>
        }
    };

    const tableLoading = {
        spinning: isLoading,
        indicator: <LoadingOutlined style={{ fontSize: 30, color: "#757575" }} spin />,
    }

    return (
        <div>
            <Helmet>
                <title>{t("label:workflows")}</title>
            </Helmet>
            <div className="flex gap-10 justify-between items-center mb-16">
                <h1>{t("label:workflows")}</h1>
                <div className="flex flex-row space-x-10">
                    {(workflowsTable.data.totalDocs > 0 || workflowsTable.data.search) && (
                        <SearchInput onSearch={(searchString) => handleSearch.callback(searchString)} value={workflowsTable.data.search} />
                    )}

                    <Filter
                        table={TablesEnum.WORKFLOWS}
                        isFilterModalShowing={isFilterModalShowing}
                        setIsFilterModalShowing={setIsFilterModalShowing}
                        onFilter={(data) => applyFilter(TablesEnum.WORKFLOWS, data)}
                        options={{ vendors, costCenters, employees, costCenters2 }}
                    />

                    <Button
                        type="primary"
                        onClick={async () => {
                            await setWorkflow(undefined)(dispatch)
                            setIsModalShowing(true)
                        }}
                        disabled={!isCreateBtnEnabled}
                    >
                        <PlusIcon />
                        <span>{t("action:create_workflows")}</span>
                    </Button>
                    <WorkflowModal isVisible={isModalShowing} onCancel={setIsModalShowing} />
                </div>
            </div>
            <div id="drag-and-drop-anchor" ref={dragAndDropAnchor} className="w-full antd-table" />

            <FilterSection table={TablesEnum.WORKFLOWS} options={{ vendors, costCenters, costCenters2, employees }} />

            {workflowsTable.data.totalDocs === 0 && !workflowsTable.isFetching ? (
                !workflowsTable.data.search ? (
                    <Empty
                        className="animation-appear"
                        image={`./icons/empty_table.svg`}
                        imageStyle={{
                            height: 150,
                            marginRight: "auto",
                            marginLeft: "auto",
                            marginTop: "15%",
                            marginBottom: "40px",
                            display: "inline-block",
                        }}
                        description={
                            <span className="text-lg font-bold mt-2 mb-0">
                                {!isTableFilterApplied(workflowsTable.data.filter) ? t("info:empty_state.workflows.title") : t("info:no_filter_data_found.title.workflow")}
                            </span>
                        }
                    >
                        <div className="flex flex-col justify-between items-center gap-y-4">
                            <p className="mb-14">
                                {!isTableFilterApplied(workflowsTable.data.filter) ? t("info:empty_state.workflows.message") : t("info:no_filter_data_found.message")}
                            </p>
                            <Button
                                type="primary"
                                onClick={async () => {
                                    await setWorkflow(undefined)(dispatch)
                                    setIsModalShowing(true)
                                }}
                                disabled={!isCreateBtnEnabled}
                            >
                                <PlusIcon />
                                <span>{t("action:create_workflows")}</span>
                            </Button>
                        </div>
                    </Empty>
                ) : (
                    <div className="animation-appear">
                        <NoSearchDataFound />
                    </div>
                )
            ) : (
                <div className="overflow-auto p-2 animation-appear">
                    <Table
                        rowKey={(workflow: Workflow) => workflow._id}
                        showHeader={false}
                        columns={columns}
                        dataSource={workflowsTable.data.docs}
                        components={{
                            body: {
                                wrapper: DraggableWrapper,
                                row: DraggableBodyRow,
                            },
                        }}
                        pagination={{
                            position: ["bottomRight"],
                            showSizeChanger: true,
                            current: workflowsTable.data.search ? 1 : workflowsTable.data.page,
                            pageSize: workflowsTable.data.limit,
                            hideOnSinglePage: false,
                            pageSizeOptions: ["5", "10", "20", "50", "100"],
                        }}
                        onChange={updateTableOnChange(TablesEnum.WORKFLOWS, workflowsTable.data, false)}
                        expandable={{
                            expandIconColumnIndex: 1,
                            expandedRowRender: (workflow: Workflow) => renderWorkflowDetails(workflow),
                            expandedRowKeys: expandedRows,
                            rowExpandable: () => true,
                            expandIcon: ({ expanded, onExpand, record }) =>
                                expanded ? (
                                    <UpOutlined style={{ fontSize: "12px", marginBottom: "6px !important" }} onClick={(e) => onExpand(record, e)} />
                                ) : (
                                    <RightOutlined style={{ fontSize: "12px" }} onClick={(e) => onExpand(record, e)} />
                                ),
                            onExpandedRowsChange: (expandedRows) => setExpandedRows(expandedRows),
                        }}
                        loading={tableLoading}
                    />
                </div>
            )}
        </div>
    )
}

export default Workflows
